import React, { useEffect, useRef, useState, lazy, Suspense } from 'react';
import SEO from '../components/SEO';
import Logo from '../components/Logo';

const MobileHome = lazy(() => import('../components/ui/mobile-home'));
type DemoComponent = React.ComponentType<{ homeFAQ: { question: string; answer: string }[] }>;

const Home: React.FC = () => {
  const homeFAQ = [
    {
      question: "What is AviaGov?",
      answer: "AviaGov is an enterprise aviation platform for operators, training organizations, Civil Aviation Authorities (CAAs), and ANSPs. Its AIM surface covers authoritative aeronautical data, eAIP, AIRAC, NOTAM and Digital NOTAM, MET, PIB, flight plans, ATS messaging, datasets and charting; organizations can also activate SMS, LMS, PANS-OPS, UTM and FlyMate."
    },
    {
      question: "Can modules be used independently, or do I need the full platform?",
      answer: "Every module works standalone. You activate only what you need today — SMS for safety management, LMS for training, eAIP for aeronautical publication, PANS-OPS for procedure design, or UTM for U-space governance. When you activate multiple modules, they communicate automatically so evidence flows between them. You're never locked into the full platform."
    },
    {
      question: "How does AviaGov improve operational efficiency?",
      answer: "By connecting safety management, training, procedures and AIM operations into a traceable loop. Teams can validate flight plans, assemble PIB briefings, manage NOTAM and AIRAC work, retain evidence and route approved actions into governed workflows without bypassing authority controls."
    },
    {
      question: "Where should an operator or ATO start?",
      answer: "Start with SMS. It's the foundation for safety management and ICAO Annex 19 compliance. Add LMS when you need to link training records to safety findings and competency evidence. Most operators and ATOs run entirely on SMS + LMS without needing the authority modules."
    },
    {
      question: "Where should a Civil Aviation Authority or ANSP start?",
      answer: "Start with eAIP to modernize your aeronautical information management and publication workflows. Add PANS-OPS to govern procedure design, validation, and obstacle assessment. When needed, layer UTM for emerging U-space operations."
    },
    {
      question: "Does AviaGov support local data sovereignty?",
      answer: "Yes. For authorities with strict data residency requirements, we offer Private Cloud and On-Premise deployment models to ensure your aeronautical data remains within national infrastructure. SaaS deployment is also available for operators seeking a faster setup."
    },
  ];

  const [isMobile, setIsMobile] = useState<boolean | null>(() => {
    if (typeof window === 'undefined') return null;
    return window.innerWidth < 768;
  });
  const [DesktopDemo, setDesktopDemo] = useState<DemoComponent | null>(null);
  const desktopDemoRequested = useRef(false);

  useEffect(() => {
    const loadDesktopDemo = () => {
      if (desktopDemoRequested.current) return;
      desktopDemoRequested.current = true;
      void import('../components/ui/demo').then((module) => {
        setDesktopDemo(() => module.default);
      });
    };

    const check = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) {
        loadDesktopDemo();
      }
    };

    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const showMobile = isMobile === true;
  const showDesktop = isMobile === false;

  return (
    <>
      <SEO
        title="Enterprise Aviation Software for CAAs, ANSPs, Operators, and ATOs"
        description="Manage SMS, LMS, eAIP, PANS-OPS, and U-Space governance in one regulated aviation platform for authorities, ANSPs, airlines, operators, and training organizations."
        faq={homeFAQ}
      />

      {showMobile ? (
        <Suspense fallback={<div className="min-h-screen bg-slate-950" aria-hidden="true" />}>
          <MobileHome homeFAQ={homeFAQ} />
        </Suspense>
      ) : showDesktop && DesktopDemo ? (
        <DesktopDemo homeFAQ={homeFAQ} />
      ) : null}

      {!showMobile && (!showDesktop || !DesktopDemo) ? <DesktopHomeFallback /> : null}

      {/*
        ── SEO & AEO TEXT PAYLOAD ──────────────────────────────────────────
        Always in DOM so search bots and answer engines (which often do not
        scroll or simulate JS interactions) can read the entire platform's capabilities.
        Visually hidden via sr-only; fully accessible.
      */}
      {isMobile !== true && (
        <div className="sr-only">
          <nav aria-label="Site links">
            <ul>
              {[
                { label: "eAIP Digital Platform",           href: "/modules/eaip" },
                { label: "Safety Management System (SMS)",  href: "/modules/sms" },
                { label: "Learning Management System (LMS)", href: "/modules/lms" },
                { label: "PANS-OPS Procedure Design",       href: "/modules/pans-ops" },
                { label: "UTM / U-Space Governance",        href: "/modules/utm" },
                { label: "FlyMate",                         href: "/modules/ai-assistant" },
                { label: "FlyMate",                         href: "/flymate" },
                { label: "Solutions",                       href: "/solutions" },
                { label: "Compliance Framework",            href: "/compliance" },
                { label: "SMS Regulatory",                  href: "/sms-regulatory" },
                { label: "SMS for Airlines",                href: "/sms-airlines" },
                { label: "LMS for Aviation Authorities",    href: "/lms-aviation-authority" },
                { label: "LMS for ANSPs and CAAs",          href: "/lms-ansp-caa" },
                { label: "LMS for Airlines",                href: "/lms-airlines" },
                { label: "Safety Governance",               href: "/safety-governance" },
                { label: "Aviation Insight Blog",           href: "/blog" },
                { label: "Public Portal Demo",             href: "/public-portal" },
                { label: "About AviaGov",                  href: "/about" },
                { label: "Team",                           href: "/team" },
                { label: "Pricing",                        href: "/pricing" },
                { label: "Contact",                        href: "/contact" },
                { label: "Privacy Policy",                 href: "/privacy" },
                { label: "Terms of Service",               href: "/terms" },
                { label: "Cookie Policy",                  href: "/cookies" },
              ].map(({ label, href }) => (
                <li key={href}><a href={href}>{label}</a></li>
              ))}
            </ul>
          </nav>
          
          <main>
            <h1>Governed aviation operations for CAAs, ANSPs, operators, airports, and training organizations</h1>
            <section>
              <h2>eAIP - Digital Aeronautical Information (ICAO Annex 15)</h2>
              <p>Operate authoritative AIM data through AIRAC planning, change sets, eAIP editions, AIP Supplements, AICs, NOTAM and Digital NOTAM lifecycles, MET and PIB briefings, ICAO flight-plan validation and submission, ATS messaging, datasets, chart products and controlled public distribution.</p>
            </section>
            <section>
              <h2>SMS - Safety Management System (ICAO Annex 19)</h2>
              <p>Manage occurrences, investigations, risk models, safety performance indicators, FRMS, corrective actions, Part 139 inspections and regulator-ready assurance packages with explicit closure gates and retained evidence.</p>
            </section>
            <section>
              <h2>LMS - Learning Management System (EASA Part-ORO / Part-CAMO)</h2>
              <p>Deliver regulated learning with course authoring, SCORM, assessments, live cohorts, ICAO competency objectives, qualification ledgers, recurrence management, compliance packs and verifiable certification.</p>
            </section>
            <section>
              <h2>PANS-OPS - Procedure Design & Assessment (ICAO Doc 8168)</h2>
              <p>Design, validate and release instrument procedures with PANS-OPS calculations, terrain and obstacle evidence, precision map tooling, assessment traceability and controlled eAIP handoff.</p>
            </section>
            <section>
              <h2>UTM - U-Space Traffic Management (USSP / Digital SORA)</h2>
              <p>Manage organization-scoped UTM and drone operations with tenant controls, volume configuration, provider integrations, certificates, audit trails and rate-limited operational access.</p>
            </section>
            <section>
              <h2>FlyMate - Grounded Aviation Assistant</h2>
              <p>FlyMate provides grounded aviation assistance across AIM, NOTAM, flight planning, procedures, safety and training. Responses remain bounded by available evidence, feedback and assurance controls; it does not autonomously approve or publish operational changes.</p>
            </section>
          </main>
        </div>
      )}
    </>
  );
};

const DesktopHomeFallback: React.FC = () => (
  <section className="min-h-screen bg-slate-950 px-6 py-28 text-white">
    <div className="mx-auto flex min-h-[60vh] max-w-5xl flex-col items-center justify-center text-center">
      <Logo size="lg" />
      <p className="mt-8 max-w-3xl text-balance text-lg text-slate-300">
        Enterprise aviation software for CAAs, ANSPs, operators, and training organizations.
      </p>
    </div>
  </section>
);

export default Home;
